/**
 * The only module that calls `crypto.subtle`: AES-GCM, HKDF, SHA-256 and ECDH, each a few lines, with the errors the
 * rest of the app knows. Nothing outside `web/src/crypto/` may reach for the primitives (ESLint enforces it).
 */
import { IV_BYTES, KEY_BITS } from './labels';
import { randomBytes } from './random';

export class CryptoUnavailableError extends Error {
  constructor() {
    super('Passwork Bridge needs the Web Cryptography API (AES-GCM). Try another browser on this device.');
    this.name = 'CryptoUnavailableError';
  }
}

export class DecryptError extends Error {
  constructor(message = 'Failed to decrypt data: authentication tag mismatch (likely wrong key or tampered data).') {
    super(message);
    this.name = 'DecryptError';
  }
}

/** The data was made by another version or suite of Bridge: a different client, not a wrong key. */
export class UnsupportedVersionError extends DecryptError {
  constructor(message = 'This was made with a different version of Bridge, or was altered on the way. Update Bridge and open the link again.') {
    super(message);
    this.name = 'UnsupportedVersionError';
  }
}

function subtleOrNull(): SubtleCrypto | null {
  const api: unknown = (globalThis as { crypto?: { subtle?: unknown } }).crypto?.subtle;
  return typeof api === 'object' && api !== null ? (api as SubtleCrypto) : null;
}

function subtle(): SubtleCrypto {
  const s = subtleOrNull();
  if (s === null) throw new CryptoUnavailableError();
  return s;
}

export function isCryptoAvailable(): boolean {
  return subtleOrNull() !== null;
}

export function concat(...parts: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(parts.reduce((sum, part) => sum + part.length, 0)));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

// ------------------------------------------------------------------- AES-GCM

/**
 * Encrypts bytes as `IV ‖ AES-256-GCM output`. `aad` (additional authenticated data) is not stored but must match on
 * decryption; every ciphertext of Bridge is bound to its place with it.
 */
export async function sealBytes(key: CryptoKey, plaintext: Uint8Array<ArrayBuffer>, aad?: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const iv = randomBytes(IV_BYTES);
  const params: AesGcmParams = aad === undefined ? { name: 'AES-GCM', iv } : { name: 'AES-GCM', iv, additionalData: aad };
  const ciphertext = new Uint8Array(await subtle().encrypt(params, key, plaintext));
  return concat(iv, ciphertext);
}

export async function openBytes(key: CryptoKey, data: Uint8Array<ArrayBuffer>, aad?: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  if (data.length <= IV_BYTES) throw new DecryptError('Invalid ciphertext length: too short to contain IV.');
  const iv = data.subarray(0, IV_BYTES);
  const body = data.subarray(IV_BYTES);
  const params: AesGcmParams = aad === undefined ? { name: 'AES-GCM', iv } : { name: 'AES-GCM', iv, additionalData: aad };
  try {
    return new Uint8Array(await subtle().decrypt(params, key, body));
  } catch {
    throw new DecryptError();
  }
}

/** AES-256-GCM with a given IV: the caller guarantees that the (key, IV) pair is never used twice. */
export async function sealWithIv(key: CryptoKey, iv: Uint8Array<ArrayBuffer>, plaintext: Uint8Array<ArrayBuffer>, aad: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: aad }, key, plaintext));
}

/** The plaintext of `ciphertext ‖ tag`, or a DecryptError when the tag, the key, the IV or the additional data differ. */
export async function openWithIv(key: CryptoKey, iv: Uint8Array<ArrayBuffer>, ciphertext: Uint8Array<ArrayBuffer>, aad: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  try {
    return new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv, additionalData: aad }, key, ciphertext));
  } catch {
    throw new DecryptError();
  }
}

// ------------------------------------------------------------------ SHA-256

export async function sha256(...parts: readonly Uint8Array[]): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await subtle().digest('SHA-256', concat(...parts)));
}

// --------------------------------------------------------------------- HKDF

/** The link key as a key that can only derive: never exported again, never used to encrypt. */
export function importHkdfKey(raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return subtle().importKey('raw', raw, 'HKDF', false, ['deriveKey']);
}

/** `HKDF-SHA-256(base, salt = empty, info)` as a non-extractable AES-256-GCM key. */
export function deriveAesKey(base: CryptoKey, info: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return subtle().deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info }, base, { name: 'AES-GCM', length: KEY_BITS }, false, ['encrypt', 'decrypt']);
}

/** `HKDF-SHA-256(material, salt = empty, info)` as a one-time AES-256-GCM key; the material is wiped. */
export async function hkdfAesKey(material: Uint8Array<ArrayBuffer>, info: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  try {
    return await deriveAesKey(await importHkdfKey(material), info);
  } finally {
    material.fill(0);
  }
}

// --------------------------------------------------------------------- ECDH

/** An ECDH P-256 pair with a private key that cannot be exported, and the public point (65 bytes). */
export async function generateEcdh(): Promise<{ privateKey: CryptoKey; point: Uint8Array<ArrayBuffer> }> {
  const pair = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
  return { privateKey: pair.privateKey, point: new Uint8Array(await subtle().exportKey('raw', pair.publicKey)) };
}

/** The peer's public point as a key, or a DecryptError: Web Crypto refuses a point that is not on the curve. */
export async function importPeerPoint(point: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  try {
    return await subtle().importKey('raw', point, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  } catch {
    throw new DecryptError('Invalid public key.');
  }
}

export async function deriveEcdhSecret(privateKey: CryptoKey, peer: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await subtle().deriveBits({ name: 'ECDH', public: peer }, privateKey, 256));
}
